import React, { useState, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import useAuth from "../../hooks/use-auth";
import styled from "styled-components";
import * as Yup from "yup";
import { Box, Button, Text } from "@vinomofo/components";
import { trackEvent } from "../../utils/track-event";
import { TrackingEventName } from "../../utils/event-tracking";
import { HideShowPasswordIcon } from "@vinomofo/icons";
import { TextField } from "../form-inputs/FormFields";

import { COLORS } from "../../styles";

export const FormInput = styled(Field)<{ isSpirit?: boolean }>`
  // color: ${({ theme }) => theme.colors.black};

  &.bottom-border {
    border: 0;
    border-bottom: 2px solid rgb(118, 118, 118);
    text-align: center;
    background: none;
    border-radius: 0;
    font-size: 18px;

    ::placeholder {
      opacity: 1;
      color: rgb(118, 118, 118);
    }

    &:focus {
      box-shadow: none;
      border-color: ${({ theme, isSpirit }) =>
        isSpirit ? COLORS.mofoGold : theme.colors.brand};

      ::placeholder {
        opacity: 0;
      }
    }
  }

  & + div {
    display: flex;
    justify-content: flex-end;
    min-height: 10px;

    p {
      padding: 3px 5px;
      background-color: #cf2c30;
      color: white;
      border-radius: 4px;
      line-height: 1.6;
      overflow-wrap: break-word;
      white-space: normal;
      width: fit-content;
    }
  }
`;

export const FormBtn = styled(Button)<{ isSpirit?: boolean }>`
  opacity: 1;

  ${({ isSpirit }) =>
    isSpirit &&
    `
    background-color: ${COLORS.mofoGold};
    border-color: ${COLORS.mofoGold};

    &:hover {
      background-color: ${COLORS.gold};
      border-color: ${COLORS.gold};
    }
  `}
`;

interface LoginFormProps {
  isSpirit: boolean;
  formLocation: string;
  guestEmail?: string | null | undefined;
  fieldColumn?: 1 | 2;
  continueAsGuest?: () => void;
}

const LoginForm = React.memo(
  ({
    isSpirit = false,
    formLocation = "Paywall",
    guestEmail = "",
    continueAsGuest,
    fieldColumn = 2,
  }: LoginFormProps) => {
    const eventName = `Login on ${formLocation} Clicked` as TrackingEventName;
    const { join, loginWithCredentials } = useAuth();
    const [revealPassword, setRevealPassword] = useState(false);
    const handleToggleClick = () => {
      setRevealPassword(!revealPassword);
    };
    const baseUrl = `${process.env.GATSBY_AUTH0_DOMAIN}/login?client=${
      process.env.GATSBY_AUTH0_CLIENT_ID
    }&tenantDomain=${
      process.env.GATSBY_AUTH0_TENANT_DOMAIN
    }&audience=${encodeURIComponent(process.env.GATSBY_AUTH0_AUDIENCE)}`;

    const loginWithParameter = async (
      values: { email: string; password: string },
      setSubmitting: (isSubmitting: boolean) => void,
      setError: (msg: string | null) => void
    ) => {
      try {
        trackEvent(eventName, {
          location: location?.pathname ?? "/",
          button: `${formLocation} Login Button`,
        });

        const delimiter = "A8k@b#Z2!x";
        const timecode = Date.now(); // UTC timestamp in milliseconds

        const combined = `${values?.email}${delimiter}${values?.password}${delimiter}${timecode}`;
        const encoded = btoa(combined);

        loginWithCredentials({ returnTo: window.location.href }, encoded);
      } catch (error) {
        console.error("Failed to connect to the server.");
        setError("Server error. Please try again later.");
      } finally {
        setSubmitting(false);
      }
    };

    const handleForgotPassword = () => {
      trackEvent(eventName, {
        location: location?.pathname ?? "/",
        button: `${formLocation} Forgot Password Button`,
      });

      let redirectUrl = baseUrl + `/#/auth/login/forgot-password`;
      if (!redirectUrl.startsWith("http")) {
        redirectUrl = `https://${redirectUrl}`;
      }
      window.location.href = redirectUrl;
    };

    const handleOtherOption = () => {
      trackEvent(eventName, {
        location: location?.pathname ?? "/",
        button: `${formLocation} Other Option Button`,
      });

      let redirectUrl = baseUrl + `/#/auth/login?returnTo=/`;
      if (!redirectUrl.startsWith("http")) {
        redirectUrl = `https://${redirectUrl}`;
      }
      window.location.href = redirectUrl;
    };

    const handleJoin = useCallback(
      (email?: string | undefined) => {
        trackEvent("Join Clicked", {
          button: "Join",
          location: location?.pathname ?? "/",
        });

        const _guestEmail = localStorage.getItem("guestEmail") as
          | string
          | undefined;
        const params: { returnTo: string; email?: string } = {
          returnTo: location.pathname || "/cart",
        };
        if (email || guestEmail)
          params.email = email || guestEmail || _guestEmail;

        join(params);
      },
      [join]
    );

    const validationSchema = Yup.object({
      email: Yup.string()
        .trim()
        .nullable()
        .email("Invalid email")
        .required("Required"),
      password: Yup.string().required("Required"),
    });

    return (
      <Formik
        enableReinitialize
        validateOnMount={true}
        initialValues={{ email: guestEmail, password: "" }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, setFieldError }) => {
          loginWithParameter(values, setSubmitting, (msg) => {
            if (msg) setFieldError("general", msg);
          });
        }}
      >
        {({ isSubmitting, errors, values, isValid }) => (
          <Form style={{ width: "100%" }}>
            <Box
              display="flex"
              justifyContent="stretch"
              flexDirection={
                fieldColumn === 1
                  ? ["column", "column", "row"]
                  : ["column", "column", "column"]
              }
              gridGap={fieldColumn === 1 ? [0, 12, 15] : 0}
            >
              <Box position={"relative"} width="100%">
                <FormInput
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  component={TextField}
                  className={fieldColumn === 1 ? "all-border" : "bottom-border"}
                  required
                  isSpirit={isSpirit}
                />
              </Box>
              <Box position={"relative"} width="100%">
                <FormInput
                  type={revealPassword ? "text" : "password"}
                  name="password"
                  placeholder="Your password"
                  component={TextField}
                  className={fieldColumn === 1 ? "all-border" : "bottom-border"}
                  required
                  isSpirit={isSpirit}
                />
                <Box position={"absolute"} right={10} top={10} tabIndex="-1">
                  <Button
                    type="button"
                    onClick={handleToggleClick}
                    hoverColor="primary"
                    color={revealPassword ? "primary" : "dorianGray"}
                  >
                    <HideShowPasswordIcon
                      isClicked={revealPassword}
                      width="24"
                      height="24"
                    />
                  </Button>
                </Box>
              </Box>
            </Box>
            {errors?.general && (
              <Box>
                <Text color={"red"} fontSize={2}>
                  {errors?.general}
                </Text>
              </Box>
            )}
            <Box
              display="flex"
              justifyContent="stretch"
              flexDirection={
                fieldColumn === 1 && guestEmail
                  ? ["column", "column", "row"]
                  : ["column", "column", "column"]
              }
              gridGap={fieldColumn === 1 && guestEmail ? [3, 12, 15] : 0}
              textAlign="center"
              alignItems="center"
              mt="8px"
              mb="20px"
              fontFamily={`"Graphik", sans-serif`}
            >
              <FormBtn
                type="submit"
                disabled={isSubmitting || !isValid}
                p="10px 30px"
                fontSize="18px"
                fontWeight={500}
                variant="primary"
                width="100%"
                lineHeight="1.2"
                borderWidth={"2px"}
                borderStyle={"solid"}
                isSpirit={isSpirit}
              >
                {isSubmitting ? "Logging in..." : "Log in"}
              </FormBtn>

              {fieldColumn === 1 && guestEmail && (
                <Button
                  type="button"
                  disabled={isSubmitting}
                  p="10px 30px"
                  fontSize="18px"
                  fontWeight={500}
                  variant="outline"
                  width="100%"
                  lineHeight="1.2"
                  onClick={continueAsGuest}
                >
                  {"Continue as guest"}
                </Button>
              )}
            </Box>
            <Box
              fontSize="12px"
              mt="10px"
              display="flex"
              justifyContent="space-between"
              fontWeight="700"
            >
              <Box
                textAlign={"left"}
                as={"a"}
                onClick={handleOtherOption}
                fontSize={"10px"}
                color={"black"}
                fontWeight={300}
                fontFamily={`"Graphik", sans-serif`}
                style={{ textDecoration: "underline", cursor: "pointer" }}
              >
                LOG-IN WITH OTHER OPTIONS
              </Box>
              <Box
                textAlign={"right"}
                as={"a"}
                onClick={handleForgotPassword}
                fontSize={"10px"}
                color={"black"}
                fontWeight={300}
                fontFamily={`"Graphik", sans-serif`}
                style={{ textDecoration: "underline", cursor: "pointer" }}
              >
                FORGOT PASSWORD
              </Box>
            </Box>
            <Box
              fontSize="12px"
              mt="20px"
              display="flex"
              justifyContent="center"
              fontWeight="700"
            >
              <Text fontSize="12px" color="black">
                NOT A MOFO YET?{" "}
                <Box
                  textAlign={"left"}
                  as={"a"}
                  onClick={() => {
                    handleJoin(values?.email || undefined);
                  }}
                  fontSize={"12px"}
                  color={"black"}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  SIGN UP
                </Box>
              </Text>
            </Box>
          </Form>
        )}
      </Formik>
    );
  }
);

LoginForm.displayName = "LoginForm";

export default LoginForm;
